import _ from 'lodash';
import React from 'react';
import { observer } from 'mobx-react-lite';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useViewportVisibility from 'hooks/use-viewport-visibility';
import useUI from 'hooks/use-ui';
import useTranslation from 'hooks/use-translation';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';

import { tracker } from 'src/utils/analytics';
import { ProductRecommendationSource } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

import { CarouselProductCard } from 'src/components/product-card';
import CarouselSlider from 'components/carousel-slider';
import EmptyLoadingState from 'components/empty-loading-state';
import { useAddToCartModal, findSingleWeightItem } from 'components/modals/add-to-cart';
import { applySettings, defaultFilters } from '../carousels.utils';
import { dimensions, CardCarouselItem, Section, GroupHeader, Heading } from '../carousels.styles';

const sortProducts = (fetched, products) =>
  _.sortBy(
    fetched,
    (a, b) =>
      _.findIndex(products, (product) => product === a?.id) - _.findIndex(products, (product) => product === b?.id)
  );

const setInternalTrackerContext = ({ index, section }) => {
  tracker.setContext({
    activeProductPosition: {
      fromCarousel: true,
      index,
      section,
      source: ProductRecommendationSource.native,
    },
  });
};

const generateSlides = ({ dispensary, sortedProducts, sectionIndex, UI, handleAddToCart }) =>
  _.map(applySettings(sortedProducts), (product, index) => (
    <CardCarouselItem key={product.id}>
      <CarouselProductCard
        dimensions={dimensions}
        dispensary={dispensary}
        product={product}
        width='100%'
        productIndex={index}
        sectionIndex={sectionIndex}
        onQuickAddClick={() => {
          UI.setProductPosition({
            productId: product.id,
            productIndex: index,
            sectionIndex,
          });

          setInternalTrackerContext({ index, section: UI.carouselLabels[sectionIndex].name });
          handleAddToCart(product);
        }}
        onClick={() => {
          UI.setProductPosition({
            productId: product.id,
            productIndex: index,
            sectionIndex,
          });

          setInternalTrackerContext({ index, section: UI.carouselLabels[sectionIndex].name });
        }}
      />
    </CardCarouselItem>
  ));

export const CustomCarousel = observer(({ section, sectionIndex }) => {
  const UI = useUI();

  const { label, products, hideTitle } = section;
  const { t } = useTranslation();
  const { dispensary } = useDispensary();
  const { ref, hasBeenVisible } = useViewportVisibility();

  const { open: openAddToCartModal } = useAddToCartModal();

  const { products: fetchedProducts, loading } = useDispensaryProducts({
    filters: { ...defaultFilters, productIds: products },
    useBatch: true,
    skip: !hasBeenVisible,
  });

  const hasProducts = fetchedProducts?.length > 0;
  if (!hasProducts && !loading && hasBeenVisible) {
    return null;
  }

  const newSectionPositions = UI?.carouselLabels ?? [];
  newSectionPositions.splice(sectionIndex, 1, { name: label });
  UI?.setCarouselLabels(newSectionPositions);

  const sortedProducts = sortProducts(fetchedProducts, products);

  const handleAddToCart = async (product) => {
    openAddToCartModal({
      ...findSingleWeightItem(product),
      product,
      dispensary,
    });
  };

  const slides = generateSlides({ dispensary, sortedProducts, sectionIndex, UI, handleAddToCart });

  return (
    <div ref={ref} style={{ minHeight: '420px' }}>
      {hasBeenVisible && (
        <EmptyLoadingState isLoading={loading} page='productCarousel'>
          <Section>
            {hideTitle !== `true` && (
              <GroupHeader>
                <Heading>{t(`dispensary.section.${label}`, `${label}`)}</Heading>
              </GroupHeader>
            )}
            <CarouselSlider>{slides}</CarouselSlider>
          </Section>
        </EmptyLoadingState>
      )}
    </div>
  );
});
